// Data caching utility for offline-like performance
const CACHE_PREFIX = 'inventory_cache_';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours max cache

export const dataCache = {
  // Set data with timestamp
  set: (key, data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        restaurantId: localStorage.getItem('selectedRestaurantId')
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Cache write failed:', e);
    }
  },

  // Get data if valid (same restaurant, not expired)
  get: (key) => {
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const { data, timestamp, restaurantId } = JSON.parse(cached);
      const currentRestaurant = localStorage.getItem('selectedRestaurantId');
      
      // Invalidate if different restaurant or expired
      if (restaurantId !== currentRestaurant) return null;
      if (Date.now() - timestamp > CACHE_EXPIRY) return null;
      
      return data;
    } catch (e) {
      return null;
    }
  },

  // Clear all cache (on logout or restaurant change)
  clear: () => {
    Object.keys(localStorage)
      .filter(key => key.startsWith(CACHE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  },

  // Clear specific key
  remove: (key) => {
    localStorage.removeItem(CACHE_PREFIX + key);
  },

  // Check if cache exists and is fresh
  isFresh: (key, maxAge = 5 * 60 * 1000) => { // 5 min default
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return false;

      const { timestamp, restaurantId } = JSON.parse(cached);
      const currentRestaurant = localStorage.getItem('selectedRestaurantId');
      
      if (restaurantId !== currentRestaurant) return false;
      return Date.now() - timestamp < maxAge;
    } catch (e) {
      return false;
    }
  }
};

// Idle timeout manager
const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
let idleTimer = null;
let onIdleCallback = null;

export const idleManager = {
  // Start monitoring for idle
  start: (onIdle) => {
    onIdleCallback = onIdle;
    idleManager.resetTimer();
    
    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, idleManager.resetTimer, { passive: true });
    });
  },

  // Reset the idle timer
  resetTimer: () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (onIdleCallback) onIdleCallback();
    }, IDLE_TIMEOUT);
  },

  // Stop monitoring
  stop: () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = null;
    
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, idleManager.resetTimer);
    });
  }
};
